import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Bot, CheckCircle2, Fingerprint, KeyRound, ShieldAlert, XCircle } from 'lucide-react';
import { Botao, Cartao, Selo } from '../componentes/base';
import { Carregando, Erro, Vazio } from '../componentes/estados';
import { Entrada, Rotulo } from '../componentes/formulario';
import { rotuloDoItem } from '../componentes/lixeira';
import { Confirmar } from '../componentes/sobreposicao';
import { TextoCortado } from '../componentes/texto-cortado';
import { useToast } from '../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, post, traduzirErro } from '../nucleo/api';
import { assinarComPasskey, passkeyCancelada, suportaPasskey, type Aprovacao, type DesafioAprovacao, type EstadoAprovacao, type PasskeyDaConta } from '../nucleo/assistentes/dados';
import { censurarJson } from '../nucleo/censura-json';
import { dataHora, dinheiro } from '../nucleo/formato';
import { rotuloAcao, rotuloCampo, rotuloEntidade, rotuloModulo, rotuloValor } from '../nucleo/rotulos';
import { recuperarFoco } from '../nucleo/foco';
import { useSessao } from '../nucleo/sessao';

/**
 * Fase 2 (W11): tela "Aprovar ação". O push do assistente traz só um título genérico e abre esta tela, que mostra o resumo
 * (com a máscara do modo discreto) e pede a confirmação: passkey (biometria do aparelho) ou, para quem não tem passkey, o
 * código do app autenticador. Recusar não pede confirmação.
 */
export const COR_ESTADO_APROVACAO: Record<EstadoAprovacao, 'laranja' | 'verde' | 'neutro' | 'perigo'> = {
  pendente: 'laranja',
  aprovada: 'verde',
  recusada: 'neutro',
  expirada: 'neutro',
  falhou: 'perigo',
};

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-xs text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-sm break-words text-tinta-1 sm:text-right">{children}</dd>
    </div>
  );
}

/** Campos da ação que será feita, com a máscara do modo discreto (dinheiro, documento, e-mail, telefone). */
function DadosDaAcao({ entrada }: { entrada: unknown }) {
  const { t } = useTranslation();
  const censurada = censurarJson(entrada ?? {}) as Record<string, unknown> | null;
  const campos = censurada && typeof censurada === 'object' ? Object.entries(censurada).filter(([k]) => k !== 'id') : [];
  if (!campos.length) return <p className="text-sm text-tinta-3">{t('assistentes.aprovar.semDados')}</p>;
  return (
    <dl className="flex flex-col gap-2 rounded-lg border border-borda bg-sup-2 p-3" data-dados-acao>
      {campos.map(([k, v]) => (
        <Linha key={k} rotulo={rotuloCampo(k)}>
          <TextoCortado linhas={3}>{rotuloValor(k, v)}</TextoCortado>
        </Linha>
      ))}
    </dl>
  );
}

function Desfecho({ a }: { a: Aprovacao }) {
  const { t } = useTranslation();
  if (a.estado === 'aprovada') {
    return <Vazio icone={<CheckCircle2 className="size-5 text-verde" aria-hidden />} titulo={t('assistentes.aprovar.feita')} texto={t('assistentes.aprovar.feitaTexto', { assistente: a.assistente_nome })} />;
  }
  if (a.estado === 'falhou') {
    const erro = a.resultado?.erro;
    return (
      <Vazio
        icone={<ShieldAlert className="size-5 text-perigo" aria-hidden />}
        titulo={t('assistentes.aprovar.falhou')}
        texto={erro ? traduzirErro(erro.codigo, erro.mensagem) : t('assistentes.aprovar.falhouTexto')}
      />
    );
  }
  if (a.estado === 'recusada') return <Vazio icone={<XCircle className="size-5" aria-hidden />} titulo={t('assistentes.aprovar.recusada')} texto={t('assistentes.aprovar.recusadaTexto')} />;
  return <Vazio icone={<XCircle className="size-5" aria-hidden />} titulo={t('assistentes.aprovar.expirada')} texto={t('assistentes.aprovar.expiradaTexto')} />;
}

function Decisao({ a }: { a: Aprovacao }) {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const passkeys = useQuery({ queryKey: ['conta', 'passkeys'], queryFn: () => get<{ itens: PasskeyDaConta[] }>('/auth/passkeys') });
  const [codigoAberto, setCodigoAberto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recusar, setRecusar] = useState(false);
  const atualizar = async () => {
    await qc.invalidateQueries({ queryKey: ['aprovacoes'] });
    await qc.invalidateQueries({ queryKey: ['assistentes'] });
  };
  const aprovar = useMutation({
    mutationFn: async (via: 'passkey' | 'codigo') => {
      if (via === 'codigo') return post<{ estado: EstadoAprovacao }>(`/aprovacoes/${a.id}/aprovar`, { codigo });
      const d = await post<DesafioAprovacao>(`/aprovacoes/${a.id}/desafio`);
      if (!d.challenge) throw new Error('sem_passkey');
      const assercao = await assinarComPasskey({ ...d, challenge: d.challenge });
      return post<{ estado: EstadoAprovacao }>(`/aprovacoes/${a.id}/aprovar`, { passkey: assercao });
    },
    onSuccess: async (r) => {
      setErro(null);
      if (r.estado === 'aprovada') toast.sucesso(t('assistentes.aprovar.feita'));
      await atualizar();
    },
    onError: async (e) => {
      if (passkeyCancelada(e)) return setErro(t('assistentes.passkeys.cancelada'));
      if (e instanceof Error && e.message === 'sem_passkey') return setErro(t('assistentes.aprovar.semPasskey'));
      setErro(mensagemDeErro(e));
      if (e instanceof ErroRequisicao && e.status === 409) await atualizar();
    },
  });
  const recusa = useMutation({
    mutationFn: () => post(`/aprovacoes/${a.id}/recusar`),
    onSuccess: async () => {
      setRecusar(false);
      toast.sucesso(t('assistentes.aprovar.recusada'));
      await atualizar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const temPasskey = (passkeys.data?.itens.length ?? 0) > 0 && suportaPasskey();
  const temTotp = !!s.seguranca?.mfa_ativo;
  return (
    <div className="flex flex-col gap-3" data-decisao>
      {erro && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erro}
        </p>
      )}
      {!temPasskey && !temTotp && !passkeys.isPending && (
        <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
          {t('assistentes.aprovar.semConfirmacao')}{' '}
          <Link to="/conta/seguranca" className="font-medium underline">
            {t('assistentes.aprovar.irSeguranca')}
          </Link>
        </p>
      )}
      {codigoAberto && (
        <Rotulo id="aprovar-codigo" rotulo={t('conta.seguranca.mfa.codigo')}>
          <Entrada
            id="aprovar-codigo"
            className="numero max-w-48"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && codigo.replace(/\D/g, '').length === 6) aprovar.mutate('codigo');
            }}
          />
        </Rotulo>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        <Botao variante="perigo" icone={<XCircle className="size-4" aria-hidden />} onClick={() => setRecusar(true)}>
          {t('assistentes.aprovar.recusar')}
        </Botao>
        {temTotp && !codigoAberto && (
          <Botao icone={<KeyRound className="size-4" aria-hidden />} onClick={() => {
              setCodigoAberto(true);
              // Review r30 visual (M2): o botão some e o campo do código aparece; o foco vai para ele em vez de cair no body.
              recuperarFoco({ reservas: ['#aprovar-codigo'] });
            }}
          >
            {t('assistentes.aprovar.usarCodigo')}
          </Botao>
        )}
        {codigoAberto && (
          <Botao variante={temPasskey ? 'secundario' : 'primario'} icone={<KeyRound className="size-4" aria-hidden />} carregando={aprovar.isPending && aprovar.variables === 'codigo'} disabled={codigo.replace(/\D/g, '').length !== 6} onClick={() => aprovar.mutate('codigo')}>
            {t('assistentes.aprovar.aprovarCodigo')}
          </Botao>
        )}
        {temPasskey && (
          <Botao variante="primario" icone={<Fingerprint className="size-4" aria-hidden />} carregando={aprovar.isPending && aprovar.variables === 'passkey'} onClick={() => aprovar.mutate('passkey')}>
            {t('assistentes.aprovar.aprovarPasskey')}
          </Botao>
        )}
      </div>
      <Confirmar
        aberto={recusar}
        aoFechar={() => setRecusar(false)}
        aoConfirmar={() => recusa.mutate()}
        carregando={recusa.isPending}
        titulo={t('assistentes.aprovar.recusarTitulo')}
        texto={t('assistentes.aprovar.recusarTexto', { assistente: a.assistente_nome })}
      />
    </div>
  );
}

export function AprovarAcao() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['aprovacoes', id], queryFn: () => get<Aprovacao>(`/aprovacoes/${id}`), retry: false });
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header className="min-w-0">
        <p className="kicker">{t('assistentes.aprovar.kicker')}</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('assistentes.aprovar.titulo')}</h1>
      </header>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        q.error instanceof ErroRequisicao && q.error.status === 404 ? (
          <Cartao>
            <Vazio titulo={t('assistentes.aprovar.naoEncontrada')} texto={t('assistentes.aprovar.naoEncontradaTexto')} acao={<Link to="/conta/assistentes" className="text-sm font-medium text-acento-forte underline">{t('assistentes.aprovar.irAssistentes')}</Link>} />
          </Cartao>
        ) : (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        )
      ) : (
        <Cartao
          titulo={
            <span className="flex min-w-0 items-center gap-2">
              <Bot className="size-4 shrink-0 text-tinta-3" aria-hidden />
              <TextoCortado linhas={2}>{t('assistentes.aprovar.pedido', { assistente: q.data.assistente_nome })}</TextoCortado>
            </span>
          }
          acao={<Selo cor={COR_ESTADO_APROVACAO[q.data.estado]}>{t(`assistentes.estadosAprovacao.${q.data.estado}`)}</Selo>}
        >
          <div className="flex flex-col gap-4" data-aprovacao={q.data.estado}>
            <dl className="flex flex-col gap-2">
              <Linha rotulo={t('assistentes.aprovar.modulo')}>{rotuloModulo(q.data.modulo)}</Linha>
              <Linha rotulo={t('assistentes.aprovar.acao')}>
                {rotuloAcao(q.data.resumo.acao)} <Selo cor="laranja">N{q.data.nivel}</Selo>
              </Linha>
              {q.data.resumo.valor_centavos !== null && <Linha rotulo={t('assistentes.aprovar.valor')}>{dinheiro(q.data.resumo.valor_centavos)}</Linha>}
              {q.data.resumo.registro_id && (
                <Linha rotulo={t('assistentes.aprovar.registro')}>
                  {/* Review r16 visual B10: o nome do registro (pelo modo discreto, como na lixeira) e o tipo; o id fica no title. */}
                  {q.data.registro ? (
                    <span className="flex min-w-0 flex-col sm:items-end" title={q.data.resumo.registro_id} data-registro-aprovacao="">
                      <TextoCortado linhas={2}>{rotuloDoItem(q.data.registro)}</TextoCortado>
                      <span className="text-xs text-tinta-3">{rotuloEntidade(q.data.registro.entidade)}</span>
                    </span>
                  ) : (
                    <span className="numero text-xs">{q.data.resumo.registro_id}</span>
                  )}
                </Linha>
              )}
              <Linha rotulo={t('assistentes.aprovar.pedidoEm')}>{dataHora(q.data.criada_em)}</Linha>
              <Linha rotulo={q.data.estado === 'pendente' ? t('assistentes.aprovar.expiraEm') : t('assistentes.aprovar.decididaEm')}>
                {dataHora(q.data.estado === 'pendente' ? q.data.expira_em : (q.data.decidida_em ?? q.data.expira_em))}
              </Linha>
              <Linha rotulo={t('assistentes.aprovar.rota')}>
                <TextoCortado className="numero text-xs">{q.data.alvo}</TextoCortado>
              </Linha>
            </dl>
            <div className="flex flex-col gap-2">
              <h2 className="kicker">{t('assistentes.aprovar.dados')}</h2>
              <DadosDaAcao entrada={q.data.entrada} />
            </div>
            {q.data.estado === 'pendente' ? <Decisao a={q.data} /> : <Desfecho a={q.data} />}
          </div>
        </Cartao>
      )}
    </div>
  );
}
